import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { createSlug, LISTING_PAGE_PARAM_TYPE_DRAFT } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import css from './CopyListingModal.module.css';
import { IconSpinner } from '..';

const CopyListingModal = ({
  isOpen,
  onClose,
  listing,
  onCopyListing,
  routeConfiguration,
  config,
  variant = 'full', // 'auto' | 'full'
}) => {

  // If listing is daycare, then don't show this modal
  if (listing?.attributes?.publicData?.listingType === 'daycare') return;

  const [copyInProgress, setCopyInProgress] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !listing) return null;

  const handleCopy = async type => {
    try {
      setCopyInProgress(type);

      // 🔐 Always pass the SDK UUID object (prevents earlier 400 errors)
      const draft = await onCopyListing(listing.id, type, config);

      const titleParts = listing.author?.attributes?.profile?.displayName?.trim().split(/\s+/);
      const baseTitle =
        titleParts.length > 1
          ? `${titleParts[0]} ${titleParts[titleParts.length - 1].charAt(0)}`
          : listing.author?.attributes?.profile?.displayName;

      const draftSlug = createSlug(baseTitle);

      const draftUuid =
        typeof draft === 'string'
          ? draft
          : draft?.uuid || draft?.id?.uuid;

      if (!draftUuid) {
        throw new Error('Draft UUID missing after copy');
      }

      history.push(
        createResourceLocatorString(
          'EditListingPage',
          routeConfiguration,
          {
            id: draftUuid,
            slug: draftSlug,
            type: LISTING_PAGE_PARAM_TYPE_DRAFT,
            tab: 'details',
          },
          {}
        )
      );
    } catch (e) {
      console.error('Copy listing failed', e);
    } finally {
      setCopyInProgress(null);
      onClose();
    }
  };

  return (
    <div className={css.backdrop} onClick={onClose}>
      <div
        className={css.modal}
        onClick={e => e.stopPropagation()}
      >
        <div className={css.title}>
          Do you also want to create a {listing.attributes.publicData.listingType === 'boarding' ? 'daycare' : 'boarding'} listing?
          <br />
          <span style={{ fontWeight: 400, fontSize: '15px' }}>
            It takes just a few more seconds
          </span>
        </div>

        {variant === 'auto' && (
          listing.attributes.publicData.listingType === 'boarding' ? (
            <div className={css.subtitle}>
              {copyInProgress ? <IconSpinner /> : (
                <>
                  <button
                    key="daycare"
                    className={css.optionCopy}
                    disabled={copyInProgress || variant === 'full'}
                    onClick={variant === 'full' ? undefined : () => handleCopy('daycare')}
                  >
                    Click here
                  </button>
                  &nbsp;to approve
                </>
              )}
            </div>
          ) : (
            <div className={css.subtitle}>
              {copyInProgress ? <IconSpinner /> : (
                <>
                  <button
                    key="boarding"
                    className={css.optionCopy}
                    disabled={copyInProgress !== null || variant === 'full'}
                    onClick={variant === 'full' ? undefined : () => handleCopy('boarding')}
                  >
                    Click here
                  </button>
                  &nbsp;to copy this listing
                </>
              )}
            </div>
          )
        )}

        {variant === 'full' && (
          <div className={css.options}>
            {['boarding', 'daycare'].map(type => (
              <button
                key={type}
                className={css.option}
                disabled={copyInProgress !== null}
                onClick={() => handleCopy(type)}
              >
                {copyInProgress === type ? (
                  <IconSpinner />
                ) : (
                  type.charAt(0).toUpperCase() + type.slice(1)
                )}
              </button>
            ))}
          </div>
        )}

        <button
          className={css.cancel}
          type="button"
          onClick={onClose}
          disabled={copyInProgress}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CopyListingModal;
